import './ad.css';
import { useState, useEffect, useContext, useRef, memo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMediaQuery } from 'react-responsive';
import Like from '../like/Like';
import { CircularProgress } from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';
import arePropsEqual from '../../arePropsEqual';
import ReactPlayer from 'react-player';
import { setJGRadioCue } from '../../pages/hitlab/hitLabRadioCue';
import { shouldShowJGRadioDock } from '../../pages/hitlab/jgRadioDockVisibility';

const getPlayerToken = (player) => player?.token || '';
const getPlayerId = (player) =>
  player?.data?.player?.playerId || player?.playerId || '';

const getHitLabSongAdTrackId = (ad = {}) => {
  const directId = String(ad.trackId || ad.hitLabRadioTrackId || '').trim();
  if (directId) return directId;
  const destination = String(ad.ctaUrl || ad.url || '').trim();
  if (!destination) return '';
  try {
    const parsed = new URL(destination, 'https://www.joegalaxy.net');
    return String(parsed.searchParams.get('track') || '').trim();
  } catch {
    return '';
  }
};

const buildOptionalAuthConfig = (player, surfaceKey) => {
  const token = getPlayerToken(player);
  return {
    ...(token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : {}),
    params: { surface: surfaceKey },
  };
};

export default memo(function Ad({
  ad,
  surfaceKey = 'legacy',
  mode = 'legacy',
  onHitLabSongAdClick,
  hitLabSongAdOpenInNewTab = false,
}) {
  const PF = process.env.REACT_APP_PUBLIC_FOLDER;
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const safeLikes = Array.isArray(ad?.likes) ? ad.likes : [];
  const safeClicks = Array.isArray(ad?.clicks) ? ad.clicks : [];
  const playerId = getPlayerId(player);
  const playerToken = getPlayerToken(player);
  const isHitLabSongAd =
    mode === 'hitlab-song' ||
    ad?.adSource === 'hitlab_plus_song_ad' ||
    ad?.adSource === 'hitlab_radio_rotation' ||
    ad?.isHitLabSongAd;
  const [like, setLike] = useState(safeLikes.length);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [views, setViews] = useState(Number(ad?.views || 0));
  const mobileView = useMediaQuery({
    query: '(max-width: 805px)',
  });
  const imgRef = useRef(null);
  const hitLabImpressionsRef = useRef(new Set());

  useEffect(() => {
    setIsLiked(!!playerId && safeLikes.includes(playerId));
  }, [playerId, safeLikes]);

  useEffect(() => {
    let isMounted = true;
    const updateViews = async () => {
      if (!isMounted) return;

      if (isHitLabSongAd) {
        if (!ad?.trackingId) return;
        const impressionKey = `${surfaceKey}:${ad.trackingId}`;
        if (hitLabImpressionsRef.current.has(impressionKey)) return;
        hitLabImpressionsRef.current.add(impressionKey);
        try {
          await axiosConn.post(
            `hitlab/song-ads/${encodeURIComponent(ad.trackingId)}/impression`,
            { surfaceKey },
            buildOptionalAuthConfig(player, surfaceKey),
          );
        } catch (err) {
          console.log(err);
        }
        return;
      }

      if (!playerToken) return;
      setIsLoading(true); // Start loading
      try {
        const response = await axiosConn.put(
          'ads/' + ad._id + '/view',
          {},
          {
            headers: {
              Authorization: `Bearer ${playerToken}`,
            },
            params: { surface: surfaceKey },
          },
        );
        if (!isMounted) return;
        setViews(response.data.views);
      } catch (err) {
        console.log(err);
      } finally {
        if (!isMounted) return;
        setIsLoading(false); // End loading
      }
    };

    updateViews();
    return () => {
      isMounted = false;
    };
  }, [ad?._id, ad?.trackingId, isHitLabSongAd, player, playerToken, surfaceKey]);

  const likeHandler = async () => {
    if (!playerToken || !playerId || isHitLabSongAd) return;
    try {
      await axiosConn.put(
        'ads/' + ad._id + '/like',
        {
          playerId,
        },
        {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        },
      );
    } catch (err) {}
    setLike(isLiked ? like - 1 : like + 1);
    setIsLiked(!isLiked);
  };

  const handleAdClick = (ad) => (e) => {
    if (isHitLabSongAd) {
      const destination = ad.ctaUrl || ad.url || '#';
      if (ad.trackingId) {
        Promise.resolve(
          axiosConn.post(
            `hitlab/song-ads/${encodeURIComponent(ad.trackingId)}/click`,
            { surfaceKey },
            buildOptionalAuthConfig(player, surfaceKey),
          ),
        ).catch((err) => console.log(err));
      }
      if (hitLabSongAdOpenInNewTab && destination !== '#') {
        return;
      }
      e.preventDefault();
      if (typeof onHitLabSongAdClick === 'function') {
        onHitLabSongAdClick(ad, destination);
        return;
      }
      const trackId = getHitLabSongAdTrackId(ad);
      const canUseDockCue =
        !!playerToken &&
        !!trackId &&
        shouldShowJGRadioDock({
          pathname: location.pathname,
          isAuthenticated: true,
        });
      if (canUseDockCue) {
        setJGRadioCue({ trackId, source: 'hitlab-song-ad' });
        return;
      }
      window.location.assign(destination);
      return;
    }
    window.open(ad.url, '_blank', 'noopener,noreferrer');
    axiosConn
      .put(
        'ads/' + ad._id + '/click',
        {},
        {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
          params: { surface: surfaceKey },
        },
      )
      .catch((err) => console.log(err));
  };

  if (isHitLabSongAd) {
    const songTitle = ad?.songTitle || ad?.title || 'JG Radio record';
    const artistName = ad?.artistName || ad?.sponsorName || ad?.playerId || 'HitLab artist';
    const label = ad?.label || 'JG Universe Song Ad';
    const destination = ad?.ctaUrl || ad?.url || '#';
    return (
      <article className="adContainerElement2 hitlab-song-ad" id="adContainerElement">
        <a
          href={destination}
          className="hitlab-song-ad-link"
          target={hitLabSongAdOpenInNewTab ? '_blank' : undefined}
          rel={hitLabSongAdOpenInNewTab ? 'noopener noreferrer' : undefined}
          style={{ textDecoration: 'none' }}
          onClick={handleAdClick(ad)}
        >
          <div className="hitlab-song-ad-cover-wrap">
            {ad?.coverArtUrl || ad?.img ? (
              <img
                className="hitlab-song-ad-cover"
                src={ad.coverArtUrl || ad.img}
                alt={`Cover art for ${songTitle} by ${artistName}`}
                ref={imgRef}
              />
            ) : (
              <div className="hitlab-song-ad-cover-fallback" aria-hidden="true">
                JG
              </div>
            )}
          </div>
          <div className="hitlab-song-ad-copy">
            <span>{label}</span>
            <strong>{songTitle}</strong>
            <small>{artistName}</small>
            <em>{ad?.ctaText || 'Hear This Record'}</em>
          </div>
        </a>
      </article>
    );
  }

  return (
    <>
      <div className="adContainerElement2" id="adContainerElement">
        {isLoading ? (
          <div className="loadingContainer">
            <CircularProgress color="inherit" size="10px" />
          </div>
        ) : (
          <>
            <a
              href={`${ad.url}`}
              target="_blank"
              rel="noreferrer"
              title={`${ad.title} ${safeClicks.length} click(s)`}
              style={{ textDecoration: 'none' }}
              onClick={handleAdClick(ad)}
            >
              {ReactPlayer.canPlay(ad.img) ? (
                <div className="adOverlay videoAd">
                  <ReactPlayer
                    url={ad.img}
                    playing={true}
                    controls={true}
                    width="100%"
                    height="100%"
                    loop
                    muted
                  />
                </div>
              ) : (
                <img
                  className="ad-img2"
                  src={`${ad.img}`}
                  alt=""
                  ref={imgRef}
                />
              )}
            </a>
            <div className="adByNameContainer">
              {ad.isBusinessSpotlight ? (
                <span className="adByName">
                  Ad Sponsored by {ad.sponsorName}
                </span>
              ) : (
                <Link className="profileLink" to={`/profile/${ad.playerId}`}>
                  <span className="adByName">Ad Sponsored by {ad.playerId}</span>
                </Link>
              )}

              <div className={!mobileView ? 'adViews' : 'adViewsMobile'}>
                <VisibilityIcon sx={{ color: 'grey', fontSize: 15 }} />
                {isNaN(views) ? 0 : views}
              </div>
            </div>
            <div className="adBottom">
              <Like
                imgRef={imgRef}
                text="+2 JGK, Yay!"
                likes={safeLikes.length}
                likesProfileImgs={[]}
                isLiked={isLiked}
                likeHandler={likeHandler}
                mobileView={mobileView}
                PF={PF}
                textOffset={-15}
              />
              <div className="adBottomRight"></div>
            </div>
          </>
        )}
      </div>
    </>
  );
}, arePropsEqual);
